import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { useCurrency } from "../../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Plus, Minus, Check, Flame } from "lucide-react";

export default function ItemCustomizationDialog({ item, open, onClose, onAddToCart }) {
  const { symbol } = useCurrency();
  const [selectedVariations, setSelectedVariations] = useState({});
  const [selectedAddons, setSelectedAddons] = useState({});
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (open && item) {
      // Auto-select first option for each variation
      const defaultVars = {};
      (item.variations || []).forEach((v) => {
        if (v.options?.length > 0) {
          defaultVars[v.variation_id] = v.options[0];
        }
      });
      setSelectedVariations(defaultVars);
      setSelectedAddons({});
      setQuantity(1);
    }
  }, [open, item]);

  if (!item) return null;

  const hasVariations = item.variations?.length > 0;
  const hasAddons = item.addon_groups?.length > 0;

  const toggleAddon = (groupId, addon) => {
    setSelectedAddons((prev) => {
      const groupAddons = prev[groupId] || [];
      const exists = groupAddons.find((a) => a.addon_id === addon.addon_id);
      if (exists) {
        return { ...prev, [groupId]: groupAddons.filter((a) => a.addon_id !== addon.addon_id) };
      }
      // Check max selection for this group
      const group = item.addon_groups.find((g) => g.group_id === groupId);
      if (group && groupAddons.length >= group.max_selection) {
        return prev;
      }
      return { ...prev, [groupId]: [...groupAddons, addon] };
    });
  };

  const isAddonSelected = (groupId, addonId) => {
    return (selectedAddons[groupId] || []).some((a) => a.addon_id === addonId);
  };

  const calculatePrice = () => {
    let price = item.price || item.base_price || 0;
    Object.values(selectedVariations).forEach((opt) => {
      price += opt.price_adjustment || 0;
    });
    Object.values(selectedAddons).flat().forEach((addon) => {
      if (!addon.is_free) price += addon.price || 0;
    });
    return price;
  };

  const handleAdd = () => {
    const variationsForCart = Object.entries(selectedVariations).map(([varId, opt]) => {
      const variation = item.variations.find((v) => v.variation_id === varId);
      return {
        variation_id: varId,
        variation_name: variation?.name || "",
        option_id: opt.option_id,
        option_value: opt.value,
        price_adjustment: opt.price_adjustment || 0,
      };
    });

    const addonsForCart = Object.entries(selectedAddons).flatMap(([groupId, addons]) => {
      const group = item.addon_groups.find((g) => g.group_id === groupId);
      return addons.map((addon) => ({
        addon_id: addon.addon_id,
        name: addon.name,
        price: addon.is_free ? 0 : (addon.price || 0),
        group_name: group?.name || "Add-ons",
      }));
    });

    const imgUrl = item.image_path
      ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${item.image_path}`
      : item.image_url;
    
    const cartItem = {
      item_id: item.item_id,
      name: item.name,
      image_url: imgUrl,
      price: item.price || item.base_price || 0,
      selected_variations: variationsForCart,
      selected_addons: addonsForCart,
    };

    for (let i = 0; i < quantity; i++) {
      onAddToCart(cartItem);
    }
    onClose();
  };

  // Check addon group minimum requirements
  const validateAddons = () => {
    for (const group of (item.addon_groups || [])) {
      const selected = (selectedAddons[group.group_id] || []).length;
      if (selected < group.min_selection) return false;
    }
    return true;
  };

  const unitPrice = calculatePrice();
  const totalPrice = unitPrice * quantity;
  const isValid = validateAddons();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-white p-0" data-testid="customization-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Customize {item.name}</DialogTitle>
          <DialogDescription>Select your preferred options</DialogDescription>
        </DialogHeader>

        {/* Item Header - Simple text only */}
        <div className="p-4 pb-0">
          <h2 className="text-lg font-bold text-gray-900">{item.name}</h2>
        </div>

        <div className="p-4 space-y-5">
          {/* Nutrition info only */}
          {item.nutritional_info?.calories > 0 && (
            <div className="flex items-center gap-4 text-[10px] text-gray-400 bg-gray-50 px-3 py-2 rounded-lg">
              <span className="flex items-center gap-1"><Flame size={12} className="text-orange-400" /> {Math.round(item.nutritional_info.calories)} kcal</span>
              <span>P: {item.nutritional_info.protein}g</span>
              <span>F: {item.nutritional_info.fat}g</span>
              <span>C: {item.nutritional_info.carbs}g</span>
            </div>
          )}

          {/* Variations */}
          {hasVariations && item.variations.map((variation) => (
            <div key={variation.variation_id} data-testid={`variation-${variation.variation_id}`}>
              <h3 className="font-semibold text-gray-900 mb-2">{variation.name}</h3>
              <div className="space-y-2">
                {variation.options.filter(o => o.is_available !== false).map((option) => {
                  const isSelected = selectedVariations[variation.variation_id]?.option_id === option.option_id;
                  return (
                    <button
                      key={option.option_id}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? "border-green-500 bg-green-50"
                          : "border-gray-100 hover:border-gray-200"
                      }`}
                      onClick={() => setSelectedVariations((prev) => ({ ...prev, [variation.variation_id]: option }))}
                      data-testid={`option-${option.option_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? "border-green-500 bg-green-500" : "border-gray-300"
                        }`}>
                          {isSelected && <Check size={12} className="text-white" />}
                        </div>
                        <span className={`text-sm ${isSelected ? "font-medium text-gray-900" : "text-gray-600"}`}>
                          {option.value}
                        </span>
                      </div>
                      {option.price_adjustment !== 0 && (
                        <span className={`text-sm ${option.price_adjustment > 0 ? "text-gray-500" : "text-green-600"}`}>
                          {option.price_adjustment > 0 ? "+" : ""}{Math.round(option.price_adjustment)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Addon Groups */}
          {hasAddons && item.addon_groups.map((group) => (
            <div key={group.group_id} data-testid={`addon-group-${group.group_id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{group.name}</h3>
                <span className="text-[10px] text-gray-500">
                  {group.min_selection > 0 ? `Min ${group.min_selection}` : "Optional"}
                  {group.max_selection < 10 ? ` · Max ${group.max_selection}` : ""}
                </span>
              </div>
              <div className="space-y-2">
                {group.addons.filter(a => a.is_available !== false).map((addon) => {
                  const selected = isAddonSelected(group.group_id, addon.addon_id);
                  return (
                    <button
                      key={addon.addon_id}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        selected
                          ? "border-green-500 bg-green-50"
                          : "border-gray-100 hover:border-gray-200"
                      }`}
                      onClick={() => toggleAddon(group.group_id, addon)}
                      data-testid={`addon-${addon.addon_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                          selected ? "border-green-500 bg-green-500" : "border-gray-300"
                        }`}>
                          {selected && <Check size={12} className="text-white" />}
                        </div>
                        <span className={`text-sm ${selected ? "font-medium text-gray-900" : "text-gray-600"}`}>
                          {addon.name}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {addon.is_free ? "Free" : `+${symbol}${Math.round(addon.price)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Quantity & Add */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-green-500 transition-colors"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="custom-qty-minus"
                >
                  <Minus size={16} />
                </button>
                <span className="text-lg font-semibold w-8 text-center" data-testid="custom-qty-value">{quantity}</span>
                <button
                  className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-green-500 transition-colors"
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="custom-qty-plus"
                >
                  <Plus size={16} />
                </button>
              </div>
              <span className="text-lg font-bold text-gray-900">{symbol}{Math.round(totalPrice)}</span>
            </div>
            <Button
              className="w-full bg-green-700 hover:bg-green-800 text-white py-3 text-base font-semibold"
              onClick={handleAdd}
              disabled={!isValid}
              data-testid="custom-add-to-cart"
            >
              Add to Cart — {symbol}{Math.round(totalPrice)}
            </Button>
            {!isValid && (
              <p className="text-xs text-red-500 text-center mt-2">Please select required options</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
